import type { Directive } from 'vue'

export interface PermDirectiveOptions {
  /**
   * `hide`（默认）：`display:none` 隐藏；`remove`：从 DOM 摘除。
   *
   * 默认不用 remove：直接摘 DOM 会让 Vue 后续的 patch 找不到节点，
   * 尤其在权限快照异步到达时，节点已经不在了，按钮再也回不来。
   */
  mode?: 'hide' | 'remove'
}

/**
 * `v-perm` 指令（附录C §2）：按钮级鉴权。
 *
 * 必须在 `mounted` **和** `updated` 都判定：权限快照是异步拉回来的，
 * 只在挂载时判定一次，就会出现"按钮先消失、权限到了也不恢复"。
 */
export function createPermDirective(
  has: (code: string) => boolean,
  options: PermDirectiveOptions = {},
): Directive<HTMLElement, string | string[]> {
  const mode = options.mode ?? 'hide'
  const originalDisplay = new WeakMap<HTMLElement, string>()

  const apply = (el: HTMLElement, value: string | string[]): void => {
    const needed = Array.isArray(value) ? value : [value]
    const allowed = needed.length === 0 || needed.some((code) => has(code))
    if (allowed) {
      if (mode === 'hide' && originalDisplay.has(el)) {
        el.style.display = originalDisplay.get(el) as string
        originalDisplay.delete(el)
      }
      return
    }
    if (mode === 'remove') {
      el.remove()
      return
    }
    if (!originalDisplay.has(el)) {
      originalDisplay.set(el, el.style.display)
    }
    el.style.display = 'none'
  }

  return {
    mounted: (el, binding) => apply(el, binding.value),
    updated: (el, binding) => apply(el, binding.value),
  }
}
